import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DraftService {
  private dbName = 'ypm_drafts';
  private storeName = 'billing_drafts';
  private db: IDBDatabase | null = null;
  private _hasDraft = new BehaviorSubject<boolean>(false);
  public hasDraft$ = this._hasDraft.asObservable();

  constructor() {
    this.initDB();
  }

  private initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };

      request.onsuccess = (event: any) => {
        this.db = event.target.result;
        this.checkIfDraftExists();
        resolve();
      };

      request.onerror = (event: any) => {
        console.error('IndexedDB error:', event.target.error);
        reject(event.target.error);
      };
    });
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.initDB();
    }
    return this.db!;
  }

  async saveDraft(data: any): Promise<string> {
    const db = await this.ensureDB();
    const id = data.id || Date.now().toString();
    data.id = id;
    data.updatedAt = new Date();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(data, id);

      request.onsuccess = () => {
        this._hasDraft.next(true);
        resolve(id);
      };
      request.onerror = (event: any) => reject(event.target.error);
    });
  }

  async getDraft(id: string): Promise<any> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(id);

      request.onsuccess = (event: any) => resolve(event.target.result);
      request.onerror = (event: any) => reject(event.target.error);
    });
  }

  async getAllDrafts(): Promise<any[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = (event: any) => {
        const drafts = event.target.result;
        // Ordenar por fecha de actualización descendente
        drafts.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        resolve(drafts);
      };
      request.onerror = (event: any) => reject(event.target.error);
    });
  }

  async deleteDraft(id: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);

      request.onsuccess = () => {
        this.checkIfDraftExists();
        resolve();
      };
      request.onerror = (event: any) => reject(event.target.error);
    });
  }

  private async checkIfDraftExists(): Promise<void> {
    const drafts = await this.getAllDrafts();
    this._hasDraft.next(drafts.length > 0);
  }
}
